import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import ShipmentTrackingModal from '../../../components/ShipmentTrackingModal';
import { checkoutService } from '../../../services/checkoutService';
import { sellerService } from '../../../services/sellerService';
import { formatPrice } from '../../../utils/formatters';

// Helper to safely convert base64 data URL to local blob URL to bypass browser iframe sandbox block
const base64ToBlobUrl = (dataUrl) => {
  if (!dataUrl || !dataUrl.startsWith('data:application/pdf;base64,')) return dataUrl;
  try {
    const base64Part = dataUrl.split(',')[1];
    const byteCharacters = atob(base64Part);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  } catch (err) {
    console.error('Failed to convert base64 to Blob URL:', err);
    return dataUrl;
  }
};

// Helper to format shipper object (Text-only for PDF)
const formatShipperInfo = (shipper) => {
  if (!shipper) return null;
  if (typeof shipper === 'string') return shipper;
  const { carrier, service_type, tracking_number } = shipper;
  
  const serviceNames = {
    'UPS': { '01': 'Next Day Air', '02': '2nd Day Air', '03': 'Ground', '12': '3 Day Select', '65': 'UPS Saver' },
    'FedEx': { 'FEDEX_GROUND': 'Ground', 'PRIORITY_OVERNIGHT': 'Priority Overnight', 'STANDARD_OVERNIGHT': 'Standard Overnight' }
  };

  const serviceName = serviceNames[carrier]?.[service_type] || service_type || 'Standard';
  const carrierName = carrier || 'Standard';
  const actionVerb = tracking_number ? 'Shipped via' : 'Shipping via';

  if (!carrier && !service_type && !tracking_number) return 'Standard';
  return `${actionVerb} ${carrierName} - ${serviceName}${tracking_number ? ` [${tracking_number}]` : ''}`;
};

// Component helper for rendering shipper info with tracking modal and external links (JSX)
const renderShipperDisplay = (shipper, item = null, onTrackingClick = null) => {
  if (!shipper) return null;
  
  let info = shipper;
  if (typeof shipper === 'string') {
    try { info = JSON.parse(shipper); } catch (e) { return shipper; }
  }

  const { carrier, service_type, tracking_number, tracking_url, label_url, cost } = info;
  
  const serviceNames = {
    'UPS': { '01': 'Next Day Air', '02': '2nd Day Air', '03': 'Ground', '12': '3 Day Select', '65': 'UPS Saver' },
    'FedEx': { 'FEDEX_GROUND': 'Ground', 'PRIORITY_OVERNIGHT': 'Priority Overnight', 'STANDARD_OVERNIGHT': 'Standard Overnight' }
  };

  const serviceName = serviceNames[carrier]?.[service_type] || service_type || 'Standard';
  const carrierName = carrier || '';
  const actionVerb = tracking_number ? 'Shipped via' : 'Shipping via';
  const costVal = parseFloat(cost);
  const costStr = !isNaN(costVal) ? ` - ${formatPrice(costVal)}` : '';

  // Determine the best tracking URL for direct external review
  let trackUrl = null;
  if (tracking_url && (tracking_url.startsWith('http') || tracking_url.includes('://'))) trackUrl = tracking_url;
  else if (label_url && (label_url.startsWith('http') || label_url.includes('://')) && !label_url.startsWith('data:')) trackUrl = label_url;
  
  if (!trackUrl && tracking_number && carrierName) {
      if (carrierName === 'FedEx') trackUrl = `https://www.fedex.com/fedextrack/?trknbr=${tracking_number}`;
      else if (carrierName === 'UPS') trackUrl = `https://www.ups.com/track?loc=en_US&tracknum=${tracking_number}`;
      else if (carrierName === 'USPS') trackUrl = `https://tools.usps.com/go/TrackConfirmAction?tLabels=${tracking_number}`;
      else if (carrierName === 'DHL') trackUrl = `https://www.dhl.com/en/express/tracking.html?AWB=${tracking_number}`;
  }

  const isDelivered = (item?.itemStatus || '').toLowerCase() === 'delivered';

  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <span>{actionVerb} {carrierName}{carrierName ? ' - ' : ''}{serviceName}{costStr}</span>
      {tracking_number && (
        <span 
          onClick={(e) => {
            e.stopPropagation();
            if (typeof onTrackingClick === 'function') {
              onTrackingClick(info, item);
            }
          }}
          className="inline-flex items-center gap-1 text-primary hover:underline font-mono font-bold cursor-pointer bg-primary/5 hover:bg-primary/10 px-1.5 py-0.5 rounded border border-primary/20 transition"
          title="Click to view full Shipment Tracking status modal"
        >
          <Icon name="Truck" size={12} className="text-primary" />
          <span>[{tracking_number}]</span>
        </span>
      )}
      {/* External Carrier Site Link */}
      {tracking_number && trackUrl && (
        <a 
          href={trackUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-gray-400 hover:text-primary transition p-0.5"
          onClick={(e) => e.stopPropagation()}
          title={`Review live updates on ${carrierName || 'carrier'} official site`}
        >
          <Icon name="ExternalLink" size={12} />
        </a>
      )}
      {isDelivered && (
        <span className="bg-emerald-100 text-emerald-800 border border-emerald-300 px-1.5 py-0.5 rounded text-[10px] font-black uppercase tracking-wider flex items-center gap-1 ml-1 shadow-sm">
          <Icon name="CheckCircle" size={10} className="text-emerald-600" /> Delivered
        </span>
      )}
    </span>
  );
};

const SellerOrderList = ({ sellerId, statusFilter, isManufacturer = false }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingItems, setProcessingItems] = useState(new Set());
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // State for Label Modal and Shipment Tracking Modal
  const [labelModalData, setLabelModalData] = useState(null);
  const [activeTracking, setActiveTracking] = useState(null);

  const handleOpenTracking = (shipper, item) => {
    let info = shipper;
    if (typeof shipper === 'string') {
      try { info = JSON.parse(shipper); } catch (e) { info = {}; }
    }
    setActiveTracking({
      carrier: info?.carrier || 'Carrier',
      trackingNumber: info?.tracking_number,
      serviceType: info?.service_type || 'Standard',
      parentItem: item
    });
  };

  useEffect(() => {
    if (sellerId) {
      if (isManufacturer) {
        loadManufacturerOrders(sellerId, statusFilter);
      } else {
        loadSellerOrders(sellerId, statusFilter);
      }
    } else {
      setLoading(false);
      setError('ID is required to load orders.');
    }
  }, [sellerId, statusFilter, isManufacturer]);

  const loadManufacturerOrders = async (userId, currentStatusFilter) => {
    try {
      setLoading(true);
      setError('');
      const fetchedOrders = await checkoutService?.getManufacturerOrders(userId, currentStatusFilter);
      setOrders(fetchedOrders || []);
    } catch (err) {
      console.error('Error loading manufacturer orders:', err);
      setError(err?.message || 'Failed to load manufacturer orders.');
    } finally {
      setLoading(false);
    }
  };

  const loadSellerOrders = async (currentSellerId, currentStatusFilter) => {
    try {
      setLoading(true);
      setError('');
      
      const fetchedOrders = await checkoutService?.getSellerOrders(currentSellerId, currentStatusFilter);
      setOrders(fetchedOrders || []);

      // Background Carrier Sync: Probe live tracking for shipped items to ensure delivered orders reflect immediately
      setTimeout(() => {
        (fetchedOrders || []).forEach(ord => {
          (ord.items || []).forEach(it => {
            let sInfo = it.shipper;
            if (typeof sInfo === 'string') {
              try { sInfo = JSON.parse(sInfo); } catch(e) {}
            }
            if (sInfo?.tracking_number && sInfo?.carrier && (it.itemStatus || '').toLowerCase() !== 'delivered') {
              fetch(`/api/v1/shipping/track/${encodeURIComponent(sInfo.carrier)}/${encodeURIComponent(sInfo.tracking_number)}`)
                .then(r => r.json())
                .then(tData => {
                  if (tData.success && (tData.is_delivered || (tData.status || '').toLowerCase() === 'delivered')) {
                    // Update order in state
                    setOrders(curr => {
                      return curr.map(co => {
                        if (co.id === ord.id) {
                          const newItems = co.items.map(ci => ci.id === it.id ? { ...ci, itemStatus: 'delivered', item_status: 'delivered' } : ci);
                          const isAllDelivered = newItems.every(ci => (ci.itemStatus || '').toLowerCase() === 'delivered');
                          return {
                            ...co,
                            status: isAllDelivered ? 'delivered' : co.status,
                            isComplete: isAllDelivered || co.isComplete,
                            items: newItems
                          };
                        }
                        return co;
                      }).filter(co => {
                        // If we are on pendingOrders tab and order is now delivered, remove it from pending view
                        if (currentStatusFilter === 'pending') {
                          return !co.isComplete && (co.status || '').toLowerCase() !== 'delivered';
                        }
                        return true;
                      });
                    });
                  }
                })
                .catch(() => {});
            }
          });
        });
      }, 400);
      
    } catch (err) {
      console.error('Error loading seller orders:', err);
      setError(err?.message || 'Failed to load seller orders.');
    } finally {
      setLoading(false);
    }
  };

  const base64ToBlob = (base64, mimeType = 'application/pdf') => {
    const byteCharacters = atob(base64.split(',')[1]);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  };

  const handlePrintLabel = (url, carrier) => {
    if (!url) return;
    
    const isUPS = carrier && carrier.toUpperCase() === 'UPS';
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    if (url.startsWith('data:image')) {
        const doc = iframe.contentWindow.document;
        doc.open();
        doc.write(`
            <html>
                <head>
                    <style>
                        @page { size: ${isUPS ? 'landscape' : 'auto'}; margin: 0; }
                        body { margin: 0; padding: 0; width: 100vw; height: 100vh; display: flex; justify-content: center; align-items: center; overflow: hidden; }
                        .rotate-container {
                            display: flex; justify-content: center; align-items: center;
                            width: 100%; height: 100%;
                        }
                        img {
                            max-width: ${isUPS ? '90vh' : '100%'}; 
                            max-height: ${isUPS ? '90vw' : '100%'}; 
                            transform: ${isUPS ? 'rotate(90deg)' : 'none'};
                            transform-origin: center;
                        }
                    </style>
                </head>
                <body>
                    <div class="rotate-container">
                        <img src="${url}" onload="setTimeout(() => { window.print(); }, 800);" />
                    </div>
                </body>
            </html>
        `);
        doc.close();
    } else {
        iframe.src = url;
        iframe.onload = () => {
            setTimeout(() => {
                try {
                    iframe.contentWindow.focus();
                    iframe.contentWindow.print();
                } catch (e) {
                    console.warn('⚠️ Sandbox blocked direct iframe printing access. Opening label in a new tab for native printing...', e);
                    // Trusted synchronous window.open bypasses pop-up blockers and lets the user print easily!
                    window.open(url, '_blank');
                }
            }, 500);
        };
    }
    
    // Clean up iframe after a delay
    setTimeout(() => {
        if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
        }
    }, 60000); 
  };

  const handlePrintAndConfirm = async () => {
    if (!labelModalData || !labelModalData.labelUrl) return;
    
    // 1. Print (with carrier for rotation)
    handlePrintLabel(labelModalData.labelUrl, labelModalData.carrierCode);
    
    // 2. Confirm Status Update
    try {
        if (labelModalData.orderItemId) {
            console.log("Confirming print for item:", labelModalData.orderItemId, "Payload:", labelModalData.shipperDetails);
            // Pass shipperDetails to save them only upon confirmation
            await checkoutService.confirmShippingLabel(labelModalData.orderItemId, labelModalData.shipperDetails);
            
            // 3. Reload to reflect status change
            setTimeout(() => {
                loadSellerOrders(sellerId, statusFilter);
            }, 2000);
        }
    } catch (e) {
        console.error("Failed to confirm print status:", e);
        alert(`Label printed, but failed to update order status. Error: ${e.message || e}`);
    }
  };

  const handleUpdateManufacturingStatus = async (orderItemId, newStatus) => {
    try {
        await sellerService.updateManufacturingStatus(orderItemId, newStatus);
        // Update state in-memory
        setOrders(prevOrders => prevOrders.map(order => {
            const updatedItems = order.items.map(item => {
                if (item.id === orderItemId) {
                    return { 
                        ...item, 
                        manufacturingStatus: newStatus, 
                        manufacturing_status: newStatus 
                    };
                }
                return item;
            });
            return { ...order, items: updatedItems };
        }));
    } catch (err) {
        console.error("Failed to update manufacturing status:", err);
        alert(`Failed to update manufacturing status. Error: ${err.message || err}`);
    }
  };

  const handleResetShipping = async () => {
    if (!labelModalData || !labelModalData.orderItemId) return;
    if (!window.confirm("Are you sure you want to void the current shipping label and reset shipping? This will allow you to generate a new label with different options.")) return;

    try {
      await checkoutService.resetShippingLabel(labelModalData.orderItemId);
      setLabelModalData(null);
      loadSellerOrders(sellerId, statusFilter);
    } catch (e) {
      console.error("Failed to reset shipping:", e);
      alert(`Failed to reset shipping. Error: ${e.message || e}`);
    }
  };

  const handleCreateLabel = async (orderItemId) => {
    if (processingItems.has(orderItemId)) return;
    
    try {
      setProcessingItems(prev => new Set(prev).add(orderItemId));
      const preferredLabelType = localStorage.getItem('seller_preferred_label_type') || 'PAPER_85X11_TOP_HALF_LABEL';
      const result = await checkoutService.createShippingLabel(orderItemId, preferredLabelType);
      console.log("Create Label Result:", result);
      
      // Handle success
      if (result.success) {
        let labelUrl = result.label_url;
        if (result.label_data) {
          const contentType = result.label_content_type || 'application/pdf';
          labelUrl = `data:${contentType};base64,${result.label_data}`;
        }
        
        // Convert to local Blob URL to bypass browser iframe sandbox restriction
        const previewUrl = base64ToBlobUrl(labelUrl);
        
        // Enrich shipper details with the label URL for persistence upon confirmation
        const fullShipperDetails = {
            ...result.shipper_details,
            label_url: labelUrl,
            shipped_at: new Date().toISOString()
        };

        setLabelModalData({
          success: true,
          isViewMode: false,
          orderItemId: orderItemId, // Store ID for confirmation
          shipperDetails: fullShipperDetails, // Store enriched details
          labelUrl: previewUrl,
          trackingNumber: result.tracking_number,
          carrierCode: result.carrier_code,
          requestLog: result.request_log,
          responseLog: result.response_log
        });
        
        // Cache label locally since we don't store base64 in DB
        try {
            localStorage.setItem(`shipping_label_${orderItemId}`, labelUrl);
            localStorage.setItem(`shipping_tracking_${orderItemId}`, result.tracking_number);
            localStorage.setItem(`shipping_carrier_${orderItemId}`, result.carrier_code);
        } catch (e) {
            console.warn("Failed to cache label details:", e);
        }

        // Symmetrical Auto-Save: Automatically confirm and save the label to the database immediately upon generation!
        // This ensures that the label and tracking number are safely saved even if they just click "Close" or exit!
        try {
            console.log("Auto-Saving shipping label to database...");
            await checkoutService.confirmShippingLabel(orderItemId, fullShipperDetails);
        } catch (saveErr) {
            console.error("Auto-Save shipping label failed:", saveErr);
        }
        
        // Manually update local state to reflect changes immediately
        setOrders(prevOrders => prevOrders.map(order => {
            const updatedItems = order.items.map(item => {
                if (item.id === orderItemId) {
                    return { 
                        ...item, 
                        itemStatus: 'shipped', // Mark as shipped in-memory!
                        shipper: {
                            carrier: result.carrier_code,
                            carrierCode: result.carrier_code,
                            tracking_number: result.tracking_number,
                            trackingNumber: result.tracking_number,
                            tracking_url: result.tracking_url,
                            trackingUrl: result.tracking_url,
                            label_url: labelUrl,
                            labelUrl: labelUrl,
                            shipped_at: new Date().toISOString(),
                            shippedAt: new Date().toISOString(),
                            service_type: item.shipper?.service_type || 'Standard',
                            serviceType: item.shipper?.service_type || 'Standard'
                        } 
                    };
                }
                return item;
            });
            return { ...order, items: updatedItems };
        }));
      } else {
        console.error("Label Creation Failed:", result);
        // Handle Soft Fail (API returned logs but failed)
        setLabelModalData({
          success: false,
          errorMessage: result.message || 'Unknown error. Check console/logs.',
          requestLog: result.request_log,
          responseLog: result.response_log
        });
      }
    } catch (err) {
      // Handle Network/System Fail
      alert(`Error creating label: ${err.message}`);
    } finally {
      setProcessingItems(prev => {
        const next = new Set(prev);
        next.delete(orderItemId);
        return next;
      });
    }
  };

  const getOrderDisplayStatus = (order) => {
    if (!order) return 'Unknown';
    const s = (order.status || '').toLowerCase();
    if (s === 'completed' || s === 'delivered') return 'Delivered';
    if (s === 'refunded') return 'Refunded';
    
    // Check if any items are delivered
    const allDelivered = order.items?.length > 0 && order.items?.every(i => (i.itemStatus || '').toLowerCase() === 'delivered');
    if (allDelivered) return 'Delivered';

    // Check if any items are shipped
    const hasShippedItems = order.items?.some(i => (i.itemStatus || '').toLowerCase() === 'shipped');
    if (hasShippedItems) {
        const allShipped = order.items?.every(i => (i.itemStatus || '').toLowerCase() === 'shipped');
        return allShipped ? 'Shipped' : 'Partially Shipped';
    }
    
    return order.status ? (order.status.charAt(0).toUpperCase() + order.status.slice(1)) : 'Unknown';
  };

  const getStatusColor = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'completed':
      case 'delivered': return 'text-emerald-700 bg-emerald-100/80 border border-emerald-300 font-bold';
      case 'shipped':
      case 'in transit': return 'text-blue-700 bg-blue-100/80 border border-blue-300 font-bold';
      case 'pending': return 'text-warning bg-warning/10 font-bold';
      case 'refunded': return 'text-error bg-error/10 font-bold';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const handleShowDetails = (order) => {
    setSelectedOrder(order);
  };

  const handleCloseDetails = () => {
    setSelectedOrder(null);
  };

  // Simplified PDF Download for Seller Dashboard - only includes seller's items
  const handleDownloadPDF = (order) => {
    try {
      const pdfContent = `
        ==========================================
        SELLER ORDER SUMMARY
        ==========================================
        
        Order #: ${order?.id}
        Date: ${new Date(order?.date)?.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })}
        Status: ${order?.status ? (order?.status?.charAt(0)?.toUpperCase() + order?.status?.slice(1)) : 'Unknown'}
        
        ==========================================
        YOUR ITEMS IN THIS ORDER
        ==========================================
        
        ${order?.items?.map((item, index) => `
        ${index + 1}. ${item?.name}
           Type: ${item?.type}
           Quantity: ${item?.quantity}
           Unit Price: ${formatPrice(item?.price)}
           Subtotal: ${formatPrice(item?.price * item?.quantity)}
           ${item?.shipper ? `${formatShipperInfo(item?.shipper)}` : ''}
        `)?.join('\n')}
        
        ==========================================
        FINANCIAL SUMMARY FOR YOUR ITEMS
        ==========================================
        
        Total for Your Items: ${formatPrice(order?.items?.reduce((sum, item) => sum + (item?.price * item?.quantity), 0))}
        
        ==========================================
        
        This summary reflects only the items you provided in Order #${order?.id}.
        
        For full order details, please refer to the customer's complete invoice.
      `;

      const blob = new Blob([pdfContent], { type: 'text/plain' });
      const url = window.URL?.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `SellerOrder-${order?.id}-Summary.txt`;
      document.body?.appendChild(link);
      link?.click();
      
      document.body?.removeChild(link);
      window.URL?.revokeObjectURL(url);
    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Loading {statusFilter} orders...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-destructive/10 border border-destructive rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="AlertCircle" size={20} className="text-destructive mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-destructive mb-1">Error Loading Orders</h3>
            <p className="text-sm text-muted-foreground">{error}</p>
            {sellerId && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadSellerOrders(sellerId, statusFilter)}
                className="mt-3"
                iconName="RefreshCw"
                iconPosition="left"
              >
                Retry
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (orders?.length === 0) {
    return (
      <div className="text-center py-12">
        <Icon name="Package" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">
          No {statusFilter ? statusFilter + ' ' : ''}orders found.
        </h3>
        <p className="text-muted-foreground">Your items from customer orders will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {orders?.map((order) => (
        <div key={order?.id} className="border border-border rounded-lg p-4 hover:bg-muted/30 transition-smooth">
          <div className="flex items-start justify-between mb-4">
            <div className="space-y-1">
              <div className="flex items-center space-x-3">
                <h3 className="text-sm font-medium text-foreground">
                  Order #{order?.id}
                </h3>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(order?.status)}`}>
                  {getOrderDisplayStatus(order)}
                </span>
              </div>
              <div className="flex items-center gap-4">
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Icon name="Calendar" size={14} />
                  {new Date(order?.date)?.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </p>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Icon name="Clock" size={14} />
                  {new Date(order?.date)?.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
            
            <div className="text-right flex-shrink-0">
              <p className="text-lg font-semibold text-foreground tabular-nums">
                Total for Your Items: {formatPrice(order?.items?.reduce((sum, item) => sum + (item?.price * item?.quantity), 0))}
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {Array.isArray(order?.items) && order?.items?.length > 0 ? (
              order?.items?.map((item, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                    <Image
                      src={item?.image}
                      alt={item?.imageAlt}
                      className="w-full h-full object-cover" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-foreground truncate">
                      {item?.name}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {item?.type} × {item?.quantity}
                    </p>
                    {item?.shipper && (
                      <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                        <Icon name="Truck" size={12} />
                        Item Shipper: {renderShipperDisplay(item?.shipper, item, handleOpenTracking)}
                      </div>
                    )}
                  </div>
                  
                  <div className="text-right flex-shrink-0 flex flex-col items-end gap-2 tabular-nums">
                    <p className="text-sm font-medium text-foreground">
                      {formatPrice(item?.price)}
                    </p>
                    
                    {(() => {
                        const info = typeof item.shipper === 'string' ? JSON.parse(item.shipper) : (item.shipper || {});
                        const hasTracking = !!info.tracking_number;
                        const itemStatus = item.itemStatus;
                        const isShipped = itemStatus === 'shipped';
                        
                        // Case 1: Pending Tab -> Show "Process & Print" if NOT shipped
                        if (statusFilter?.toLowerCase() === 'pending' && !isShipped) {
                             const isCNC = !!item.componentId || !!item.component_id;
                             const currentStatus = item.manufacturingStatus || item.manufacturing_status || 'Pending';
                             const steps = isCNC 
                               ? ['Pending', 'In CNC Machine', 'Post Processing & Cure', 'Ready for Shipment']
                               : ['Pending', 'In 3D Print Now', 'Post Processing & Cure', 'Ready for Shipment'];

                             return (
                              <div className="flex flex-row items-center gap-2 mt-1">
                                <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-semibold">
                                  <span>Mfg Status:</span>
                                  <select
                                    value={currentStatus}
                                    onChange={(e) => handleUpdateManufacturingStatus(item.id, e.target.value)}
                                    className="h-9 px-3 py-1 bg-background border border-border rounded text-xs font-bold text-foreground cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary"
                                  >
                                    {steps.map(step => (
                                      <option key={step} value={step}>{step}</option>
                                    ))}
                                  </select>
                                </div>

                                <Button
                                  variant="accent"
                                  size="sm"
                                  iconName="Printer"
                                  onClick={() => handleCreateLabel(item.id)}
                                  disabled={processingItems.has(item.id)}
                                  className="h-9 font-bold whitespace-nowrap bg-orange-600 hover:bg-orange-700 text-white flex items-center justify-center"
                                >
                                  {processingItems.has(item.id) ? 'Processing...' : 'Process & Print Label'}
                                </Button>
                              </div>
                             );
                        }

                        // Case 2: Shipped OR Not Pending -> Show "View Label"
                        // Shows if item is shipped (regardless of tracking) OR if we are in non-pending tab (history)
                        if (isShipped || (hasTracking && statusFilter?.toLowerCase() !== 'pending')) {
                          return (
                            <Button
                                variant="outline"
                                size="sm"
                                iconName="FileText"
                                onClick={() => {
                                   const storedLabel = info.label_url || localStorage.getItem(`shipping_label_${item.id}`);
                                   const storedTracking = info.tracking_number || info.trackingNumber || localStorage.getItem(`shipping_tracking_${item.id}`) || '794862013231';
                                   const storedCarrier = info.carrier || info.carrierCode || localStorage.getItem(`shipping_carrier_${item.id}`) || 'FedEx';
                                   setLabelModalData({
                                     success: true,
                                     isViewMode: true,
                                     orderItemId: item.id,
                                     labelUrl: base64ToBlobUrl(storedLabel), 
                                     trackingUrl: info.tracking_url || info.trackingUrl,
                                     trackingNumber: storedTracking,
                                     carrierCode: storedCarrier
                                   });
                                }}
                                className="text-blue-600 border-blue-200 hover:bg-blue-50 font-bold"
                              >
                                View Label
                              </Button>
                          );
                        }
                        return null;
                    })()}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-sm text-muted-foreground p-4 text-center">No items found for this order.</div>
            )}
          </div>

          <div className="flex items-center justify-end mt-4 pt-4 border-t border-border gap-3">
            <Button 
              variant="outline" 
              size="sm" 
              iconName="Download"
              onClick={() => handleDownloadPDF(order)}
            >
              Download Summary
            </Button>
          </div>
        </div>
      ))}

      {/* Label Created/View Modal */}
            {labelModalData && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                <div 
                    className="bg-card rounded-lg border border-border shadow-lg max-w-5xl w-full flex flex-col h-[80vh]"
                    style={{ maxWidth: '1100px', height: '80vh' }}
                >
                  <div className={`p-4 border-b border-border flex items-center justify-between ${labelModalData.success ? '' : 'bg-red-50'}`}>
                    <div>
                      <h3 className={`text-lg font-bold flex items-center gap-2 ${labelModalData.success ? 'text-green-600' : 'text-red-600'}`}>
                        <Icon name={labelModalData.success ? "CheckCircle" : "AlertTriangle"} size={20} />
                        {labelModalData.success ? (labelModalData.isViewMode ? "Shipping Label" : "Label Created!") : "Creation Failed"}
                      </h3>
                      {labelModalData.success && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {labelModalData.carrierCode} Tracking: {labelModalData.trackingNumber}
                        </p>
                      )}
                    </div>
                    <button onClick={() => setLabelModalData(null)} className="text-muted-foreground hover:text-foreground">
                      <Icon name="X" size={24} />
                    </button>
                  </div>
                  
                  <div className="flex-1 overflow-hidden bg-muted/20 p-4 relative flex items-center justify-center">
                    
                    {!labelModalData.success && (
                      <div className="absolute top-4 left-4 right-4 bg-white p-4 rounded border border-red-200 text-sm text-red-700 z-20">
                        <strong>Error:</strong> {labelModalData.errorMessage}
                      </div>
                    )}
      
                    {/* Debug Logs Section - Collapsed at bottom if needed */}
                    {(labelModalData.requestLog || labelModalData.responseLog) && !labelModalData.success && (
                      <div className="absolute bottom-4 left-4 max-w-md z-20 opacity-90">
                          <details className="text-xs bg-white border p-2 rounded shadow">
                            <summary className="cursor-pointer font-medium text-blue-600">Debug Logs</summary>
                            <pre className="mt-2 max-h-32 overflow-auto">{labelModalData.responseLog || 'N/A'}</pre>
                          </details>
                      </div>
                    )}
      
                      {labelModalData.success && (
                        <div className="w-full h-full flex items-center justify-center overflow-auto bg-white border border-border">
                          {labelModalData.labelUrl ? (
                            <>
                                {labelModalData.labelUrl.startsWith('data:image') ? (
                                                <img 
                                                    src={labelModalData.labelUrl} 
                                                    alt="Shipping Label" 
                                                    className="origin-center"
                                                    style={{ 
                                                        display: 'block', 
                                                        margin: '0 auto', 
                                                        maxHeight: '60vh', 
                                                        maxWidth: '60vw', 
                                                        border: '1px solid #eee',
                                                        transform: 'rotate(90deg)'
                                                    }}
                                                />
                                ) : (
                                    <iframe 
                                    src={labelModalData.labelUrl} 
                                    className="w-full h-full border-0" 
                                    title="Shipping Label"
                                    >
                                       Contact Technical Support to resolve.
                                    </iframe>
                                )}
                                <a 
                                    href={labelModalData.labelUrl} 
                                    download={`Label-${labelModalData.trackingNumber || 'shipping'}`}
                                    className="absolute bottom-4 right-4 z-30 text-xs bg-white text-blue-600 border border-blue-200 px-3 py-1.5 rounded shadow-sm hover:bg-blue-50 font-medium"
                                    title="Download raw label file"
                                >
                                    Download Label File
                                </a>
                            </>
                          ) : (
                            <div className="text-muted-foreground italic">
                               Label image not available.
                            </div>
                          )}
                        </div>
                      )}                  
                  </div>
      
                  <div className="p-6 border-t border-border flex justify-end gap-3 bg-background">
                    <Button 
                      variant="outline" 
                      onClick={() => setLabelModalData(null)}
                    >
                      Close
                    </Button>
                    
                    {labelModalData.success && (
                      <div className="flex gap-2">
                        {labelModalData.isViewMode && (
                          <Button 
                            variant="outline" 
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={handleResetShipping}
                          >
                            Reset Shipping
                          </Button>
                        )}
                        <Button 
                          variant="default" 
                          iconName="Printer" 
                          className="bg-blue-600 hover:bg-blue-700"
                          onClick={labelModalData.isViewMode ? () => handlePrintLabel(labelModalData.labelUrl, labelModalData.carrierCode) : handlePrintAndConfirm}
                        >
                          {labelModalData.isViewMode ? "Reprint Label" : "Print Label"}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            {/* Shipment Tracking Modal */}
            <ShipmentTrackingModal
              isOpen={!!activeTracking}
              onClose={() => setActiveTracking(null)}
              carrier={activeTracking?.carrier}
              trackingNumber={activeTracking?.trackingNumber}
              serviceType={activeTracking?.serviceType}
              parentItem={activeTracking?.parentItem}
              onDelivered={({ trackingNumber }) => {
                setOrders(prev => prev.map(o => {
                  const updatedItems = o.items.map(it => {
                    let itTrack = it.shipper?.tracking_number;
                    if (!itTrack && typeof it.shipper === 'string') {
                      try { itTrack = JSON.parse(it.shipper)?.tracking_number; } catch(e) {}
                    }
                    if (itTrack === trackingNumber || (activeTracking?.parentItem && it.id === activeTracking.parentItem.id)) {
                      return { ...it, itemStatus: 'delivered', item_status: 'delivered' };
                    }
                    return it;
                  });
                  const isAllDelivered = updatedItems.every(it => (it.itemStatus || '').toLowerCase() === 'delivered');
                  return {
                    ...o,
                    isComplete: isAllDelivered || o.isComplete,
                    status: isAllDelivered ? 'delivered' : o.status,
                    items: updatedItems
                  };
                }).filter(o => {
                  if (statusFilter === 'pending') {
                    return !o.isComplete && (o.status || '').toLowerCase() !== 'delivered';
                  }
                  return true;
                }));
              }}
            />
    </div>
  );
};

export default SellerOrderList;
